/* eslint-disable no-nested-ternary */
import React, { useEffect, useState } from "react";
import {
    Box,
    Grid,
    Card,
    CardHeader,
    CardContent,
    Stack,
    Typography,
    Chip,
    Divider,
    Avatar,
    Tooltip,
} from "@mui/material";
import { Users, Target, Reply, Mail, FileText, LayoutTemplate } from "lucide-react";
import { doc, getDoc } from "firebase/firestore";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { db } from "../../../../../../firebase.config";

/**
 * Proposition visuelle — Résumé d'une campagne e-mail
 * Technologies: React + Material UI + lucide-react
 * 
 * Props attendues:
 *  - audienceType: 'all' | 'segments'
 *  - populations: [{ id, name, count }]
 *  - subject: string
 *  - replyTo: string (adresse de réponse)
 *  - preheader?: string
 *  - emailHtml: string (contenu HTML de l'e-mail)
 *  - notes?: string
 */

const nf = (n) => new Intl.NumberFormat("fr-FR").format(n ?? 0);

function InfoRow({ icon: Icon, label, value, dense = false }) {
    return (
        <Stack direction="row" spacing={1} alignItems="center">
            <Avatar sx={{ width: 28, height: 28, bgcolor: "#F5EFFA", color: "#5E17EB" }}>
                <Icon size={16} />
            </Avatar>
            <Box>
                <Typography variant={dense ? "caption" : "subtitle2"} color="text.secondary">
                    {label}
                </Typography>
                <Typography variant={dense ? "body2" : "body1"}>{value || "—"}</Typography>
            </Box>
        </Stack>
    );
}

export default function EmailCampaignSummary({
    audienceType = "segments",
    populations = [
        { id: "seg1", name: "Jeunes actifs", count: 120 },
        { id: "seg2", name: "Fans Afrobeat", count: 85 },
    ],
    subject = "[Rappel] Votre événement démarre bientôt ✨",
    replyTo = "contact@organisateur.com",
    preheader = "Places limitées, profitez de -10% jusqu'à ce soir.",
    emailHtml = `<!doctype html><html><head><meta charset='utf-8'/><style>body{font-family:Inter,system-ui,sans-serif;margin:0} .wrap{padding:24px} .hero{background:#5E17EB10;border:1px solid #eee;border-radius:12px;padding:16px} .btn{display:inline-block;padding:12px 16px;border-radius:8px;background:#5E17EB;color:#fff;text-decoration:none}</style></head><body><div class="wrap"><h2>Soirée Live - Abidjan</h2><p>On vous attend vendredi ! 🎫</p><div class='hero'><p>Dernières places disponibles. Réservez maintenant.</p><a class='btn' href='#'>Réserver</a></div><p style='color:#6b7280;font-size:12px;margin-top:24px'>Vous recevez cet e-mail car vous suivez l'organisateur.</p></div></body></html>`,
    notes = "Données de démonstration pour l'aperçu.",
    campagnId, mailContent, campagnData
}) {
    const totalTargets = populations?.reduce((a, b) => a + (b?.count || 0), 0);

    const [loading, setLoading] = useState(false);
    const [event, setEvent] = useState(null);       // null au lieu de {}
    const [audiences, setAudiences] = useState([]);

    useEffect(() => {
        let isMounted = true;
        (async () => {
            try {
                setLoading(true);
                // reset
                if (isMounted) setAudiences([]);

                // ---- Récup event ----
                if (campagnData?.eventID) {
                    const eventRef = doc(db, 'evenement', campagnData.eventID);
                    const eventSnap = await getDoc(eventRef);
                    if (eventSnap.exists() && isMounted) {
                        // on stocke directement les données (pas un snapshot)
                        setEvent({ id: eventSnap.id, ...eventSnap.data() });
                    }
                } else if (isMounted) {
                    setEvent(null);
                }

                // ---- Récup populations ----
                if (Array.isArray(campagnData?.audiences) && campagnData.audiences.length > 0) {
                    const reads = campagnData.audiences.map(async (popId) => {
                        const ref = doc(db, `organisations/${campagnData.organisationId}/crm/${campagnData.organisationId}/populations`, String(popId));
                        const snap = await getDoc(ref);
                        return snap.exists() ? { id: snap.id, ...snap.data() } : null;
                    });
                    const results = (await Promise.all(reads)).filter(Boolean);
                    if (isMounted) setAudiences(results);
                }
            } catch (error) {
                console.error(error);
            } finally {
                if (isMounted) setLoading(false);
            }
        })();
        return () => { isMounted = false; };
    }, [campagnData]);

    const hasPromoEmail = campagnData?.campagneType === 0;
    const audienceTypeAll = campagnData?.audienceType === 0;
    const audienceTypePop = campagnData?.audienceType === 1;

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, width: '100%' }}>
            <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} spacing={2} sx={{ mb: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Avatar sx={{ bgcolor: "#5E17EB", width: 36, height: 36 }}>
                        <LayoutTemplate size={18} />
                    </Avatar>
                    <Box>
                        <Typography variant="h6">Résumé</Typography>
                        <Typography variant="body2" color="text.secondary">Cible, objet, contact de retour & aperçu du contenu</Typography>
                    </Box>
                </Stack>
            </Stack>

            <Grid container spacing={2} alignItems="stretch">
                {/* Colonne gauche : méta */}
                <Grid item xs={12} md={5} sx={{ display: "flex" }}>
                    <Card sx={{ width: "100%", borderRadius: 2 }}>
                        <CardHeader title={<Typography variant="subtitle1">Paramètres</Typography>} sx={{ pb: 0 }} />
                        <CardContent>
                            <Stack spacing={2}>
                                {/* Cible */}
                                <Box>
                                    <Typography variant="overline" color="text.secondary">Audience Cible</Typography>
                                    <Stack spacing={1} mt={0.5}>
                                        <InfoRow icon={Target} label="Type" value={campagnData?.campagneType === 1 ? "Tous les participants à l'evenement" : audienceType === "all" ? "Tous mes contacts (newsletter)" : "Populations sélectionnées"} />
                                        {audienceType === "segments" && (
                                            <Stack direction="row" spacing={1} flexWrap="wrap">
                                                {audiences.map((p) => (
                                                    <Chip key={p.id} label={`${p.name} • ${nf(p.count)}`} size="small" sx={{ borderRadius: 2 }} />
                                                ))}
                                            </Stack>
                                        )}
                                        {campagnData?.campagneType === 1 && (
                                            <Stack spacing={1}>
                                                <Typography variant="body1"><strong>Événement</strong></Typography>

                                                {event ? (
                                                    <Stack direction="row" spacing={1} component="li" alignItems="center">
                                                        {event?.idVisuel ? (
                                                            <LazyLoadImage
                                                                src={event.idVisuel}
                                                                alt={event.nomEvent || 'visuel événement'}
                                                                className="img-lazy"
                                                                width={70}
                                                                height={70}
                                                                style={{ objectFit: 'cover' }}
                                                                effect="blur"
                                                            />
                                                        ) : (
                                                            <Box sx={{ width: 70, height: 70, bgcolor: '#eee', borderRadius: 1 }} />
                                                        )}
                                                        <Box>
                                                            <Typography variant="subtitle1">{event?.nomEvent || '—'}</Typography>
                                                            {/* <Typography variant="body2" color="textSecondary">{getTimeDifference(event.dateDebut)}</Typography> */}
                                                            {typeof event?.participant === 'number' && (
                                                                <Typography variant="body2" color="textSecondary">
                                                                    {event.participant} participants
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </Stack>
                                                ) : (
                                                    <Box sx={{ p: 2, bgcolor: '#FFADB0', borderRadius: '10px' }}>
                                                        <Typography variant="body2">Aucun événement disponible</Typography>
                                                    </Box>
                                                )}
                                            </Stack>
                                        )}
                                        <Typography variant="caption" color="text.secondary">
                                            Total cible estimé : {nf(totalTargets)} contacts
                                        </Typography>
                                    </Stack>
                                </Box>

                                <Divider sx={{ my: 1 }} />

                                {/* Objet & preheader */}
                                <Box>
                                    <Typography variant="overline" color="text.secondary">Objet</Typography>
                                    <Stack spacing={1} mt={0.5}>
                                        <InfoRow icon={Mail} label="Objet de l'e-mail" value={campagnData?.object} />
                                    </Stack>
                                </Box>

                                <Divider sx={{ my: 1 }} />

                                {/* Contact de retour */}
                                <Box>
                                    <Typography variant="overline" color="text.secondary">Contact de retour</Typography>
                                    <InfoRow icon={Reply} label={campagnData?.sender?.name} value={campagnData?.sender?.email} />
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Colonne droite : aperçu contenu */}
                <Grid item xs={12} md={7} sx={{ display: "flex" }}>
                    <Card sx={{ width: "100%", borderRadius: 2, display: "flex", flexDirection: "column" }}>
                        <CardHeader title={<Typography variant="subtitle1">Contenu du mail</Typography>} sx={{ pb: 0 }} />
                        <CardContent sx={{ flex: 1, minHeight: 420, display: "flex", flexDirection: "column" }}>
                            {/* En-tête miniature */}
                            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                                <Avatar sx={{ bgcolor: "#F5EFFA", color: "#5E17EB" }}>
                                    <Mail size={16} />
                                </Avatar>
                                <Box>
                                    <Typography variant="subtitle2">{campagnData?.object}</Typography>

                                </Box>
                            </Stack>

                            <Box sx={{ flex: 1, minHeight: 0, border: "1px solid #eee", borderRadius: 2, overflow: "hidden", boxShadow: 0 }}>
                                <iframe title="Aperçu e-mail" srcDoc={mailContent?.data()?.html} style={{ width: "100%", height: "100%", border: "none", minHeight: 360 }} />
                            </Box>

                            {notes && (
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                                    * {notes}
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
}
